import { watch } from 'vue'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useFeatureFlagsStore } from '@/store/feature-flags'
import {
  initFeatureFlags,
  identifyFeatureFlags
} from '@/app/core/services/feature-flags'

/**
 * Configura Flagsmith en el arranque de la app.
 *
 * Flujo:
 * 1. init() del SDK con onChange -> sincroniza el store reactivo.
 * 2. identify() inicial si ya hay sesion persistida (payroll activo).
 * 3. watch sobre el payroll activo -> re-identify al cambiar de compania.
 *
 * El identity es por COMPANIA (`${workspaceId}:${companyCode}`), no por
 * usuario, porque un usuario opera sobre multiples companias del tenant.
 * `changeSelectedPayroll` (selector del header) cambia el payroll sin recargar
 * la pagina, por lo que el watch es imprescindible para re-evaluar las flags.
 */
export async function configureFlagsmith(): Promise<void> {
  const ffStore = useFeatureFlagsStore()

  await initFeatureFlags(() => ffStore.syncFromSdk())
  ffStore.syncFromSdk()

  const userStore = useUserStore()
  const payrollStore = usePayrollStore()

  const reidentify = async (): Promise<void> => {
    const workspaceId = userStore.user?.workspaceId
    const company = payrollStore.payroll?.company
    const country = payrollStore.payroll?.country
    if (!workspaceId || !company?.code) return
    await identifyFeatureFlags({
      workspaceId,
      companyCode: company.code,
      countryIsoCode: country?.iso_code ?? ''
    })
  }

  // Identifica si ya hay una sesion activa (estado persistido en localStorage).
  await reidentify()

  // Re-identifica cada vez que cambia el payroll (y por ende la compania/pais).
  watch(() => payrollStore.payroll, reidentify)
}
